import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  BugReport as BugReportIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import api, { Partner, Tenant, TenantCreateRequest, TenantUpdateRequest } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { PermissionGate, AdminOnly } from '../components/PermissionGate';
import { UserManagement } from '../components/UserManagement';
import { UsersView } from '../components/UsersView';
import AllowList from '../components/AllowList';
import AttackTypes from './admin/AttackTypes';
import AuditLog from './admin/AuditLog';
import TenantDeleteDialog from '../components/TenantDeleteDialog';
import { PageTransition } from '../components/PageTransition';
import { useNotification } from '../contexts/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Partners: React.FC = () => {
  const { isAdmin, permissions, hasPermission } = usePermissions();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);

  // Partner dialog state
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [partnerForm, setPartnerForm] = useState({ name: '' });

  // Tenant dialog state
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [tenantForm, setTenantForm] = useState({ name: '', partner_id: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<{ id: string; name: string } | null>(null);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    showNotification(message, severity);
  }, [showNotification]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [partnersData, tenantsData] = await Promise.all([
        api.getPartners(),
        api.getTenants()
      ]);
      setPartners(partnersData);
      setTenants(tenantsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!isAdmin && (newValue === 2 || newValue === 4)) {
      setTabValue(0);
    } else {
      setTabValue(newValue);
    }
  };

  // Tenant admin (non-global) sees only Audit Log; keep them on that single tab
  const auditLogTabIndex = isAdmin ? 5 : 0;
  React.useEffect(() => {
    if (!isAdmin && hasPermission('view_audit_log')) {
      setTabValue(0);
    }
  }, [isAdmin, hasPermission]);

  // Partner management functions
  const handlePartnerDialogOpen = (partner?: Partner) => {
    if (partner) {
      setEditingPartner(partner);
      setPartnerForm({ name: partner.name });
    } else {
      setEditingPartner(null);
      setPartnerForm({ name: '' });
    }
    setPartnerDialogOpen(true);
  };

  const handlePartnerDialogClose = () => {
    setPartnerDialogOpen(false);
    setEditingPartner(null);
    setPartnerForm({ name: '' });
  };

  const handlePartnerSubmit = async () => {
    if (!partnerForm.name.trim()) {
      showSnackbar('Please enter a partner name', 'error');
      return;
    }

    try {
      if (editingPartner) {
        await api.updatePartner(editingPartner.id, { name: partnerForm.name });
        showSnackbar('Partner updated successfully', 'success');
      } else {
        await api.createPartner({ name: partnerForm.name });
        showSnackbar('Partner created successfully', 'success');
      }
      handlePartnerDialogClose();
      fetchData();
    } catch (error) {
      console.error('Error saving partner:', error);
      showSnackbar('Error saving partner', 'error');
    }
  };

  const handlePartnerDelete = async (partnerId: string) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await api.deletePartner(partnerId);
        showSnackbar('Partner deleted successfully', 'success');
        fetchData();
      } catch (error) {
        console.error('Error deleting partner:', error);
        showSnackbar('Error deleting partner', 'error');
      }
    }
  };

  // Tenant management functions
  const handleTenantDialogOpen = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      setTenantForm({ name: tenant.name, partner_id: tenant.partner_id || '' });
    } else {
      setEditingTenant(null);
      setTenantForm({ name: '', partner_id: '' });
    }
    setTenantDialogOpen(true);
  };

  const handleTenantDialogClose = () => {
    setTenantDialogOpen(false);
    setEditingTenant(null);
    setTenantForm({ name: '', partner_id: '' });
  };

  const handleTenantSubmit = async () => {
    if (!tenantForm.name.trim()) {
      showSnackbar('Please enter a tenant name', 'error');
      return;
    }

    try {
      if (editingTenant) {
        const updateRequest: TenantUpdateRequest = {
          name: tenantForm.name,
          partner_id: tenantForm.partner_id || undefined
        };
        await api.updateTenant(editingTenant.id, updateRequest);
        showSnackbar('Tenant updated successfully', 'success');
      } else {
        const createRequest: TenantCreateRequest = {
          name: tenantForm.name,
          partner_id: tenantForm.partner_id || undefined
        };
        await api.createTenant(createRequest);
        showSnackbar('Tenant created successfully', 'success');
      }
      handleTenantDialogClose();
      fetchData();
    } catch (error) {
      console.error('Error saving tenant:', error);
      showSnackbar('Error saving tenant', 'error');
    }
  };

  const handleTenantDelete = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setTenantToDelete({ id: tenant.id, name: tenant.name });
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;
    
    try {
      // The dialog will handle the actual deletion with selected resources
      // We just need to refresh the data after successful deletion
      fetchData();
      showSnackbar('Tenant deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting tenant:', error);
      showSnackbar('Error deleting tenant', 'error');
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTenantToDelete(null);
  };

  const getPartnerName = (partnerId?: string) => {
    if (!partnerId) return 'No Partner';
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.name : 'Unknown Partner';
  };

  return (
    <PermissionGate permission="view_admin_panel" fallback={
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" color="text.secondary" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You need admin panel access to view this page.
        </Typography>
      </Box>
    }>
      <PageTransition>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            {isAdmin ? 'Global Admin Management' : 'Audit Log'}
          </Typography>

      {loading && (
        <LinearProgress sx={{ mb: 3 }} />
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="management tabs">
          {isAdmin && (
            <Tab 
              icon={<BusinessIcon />} 
              label="Partners" 
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<PeopleIcon />} 
              label="Tenants" 
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<GroupIcon />} 
              label="Users" 
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<SecurityIcon />} 
              label="Allow List" 
              iconPosition="start"
            />
          )}
          {isAdmin && (
            <Tab 
              icon={<BugReportIcon />} 
              label="Attack Types" 
              iconPosition="start"
            />
          )}
          {hasPermission('view_audit_log') && (
            <Tab 
              icon={<HistoryIcon />} 
              label="Audit Log" 
              iconPosition="start"
            />
          )}
        </Tabs>

        {isAdmin && (
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Partners</Typography>
            <AdminOnly>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handlePartnerDialogOpen()}
              >
                Add Partner
              </Button>
            </AdminOnly>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>{partner.name}</TableCell>
                    <TableCell>{partner.id}</TableCell>
                    <TableCell align="right">
                      <AdminOnly>
                        <IconButton onClick={() => handlePartnerDialogOpen(partner)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handlePartnerDelete(partner.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </AdminOnly>
                      <UserManagement
                        resourceType="partner"
                        resourceId={partner.id}
                        resourceName={partner.name}
                        onUserAdded={fetchData}
                        onUserRemoved={fetchData}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        )}

        {isAdmin && (
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Tenants</Typography>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleTenantDialogOpen()}
              >
                Add Tenant
              </Button>
            )}
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>{tenant.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getPartnerName(tenant.partner_id)} 
                        color={tenant.partner_id ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{tenant.id}</TableCell>
                    <TableCell align="right">
                      <AdminOnly>
                        <IconButton onClick={() => handleTenantDialogOpen(tenant)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleTenantDelete(tenant.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </AdminOnly>
                      <UserManagement
                        resourceType="tenant"
                        resourceId={tenant.id}
                        resourceName={tenant.name}
                        onUserAdded={fetchData}
                        onUserRemoved={fetchData}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        )}

        {isAdmin && (
        <TabPanel value={tabValue} index={2}>
          {isAdmin ? (
            <UsersView />
          ) : (
            <Box>
              <Typography variant="h6">Access Denied</Typography>
              <Typography variant="body2" color="text.secondary">
                Only administrators can view user management.
              </Typography>
            </Box>
          )}
        </TabPanel>
        )}

        {isAdmin && (
        <TabPanel value={tabValue} index={3}>
          {isAdmin ? (
            <AllowList isSuperUser={true} />
          ) : (
            <Box>
              <Typography variant="h6">Access Denied</Typography>
              <Typography variant="body2" color="text.secondary">
                Only administrators can view allow list management.
              </Typography>
            </Box>
          )}
        </TabPanel>
        )}

        {isAdmin && (
        <TabPanel value={tabValue} index={4}>
          {isAdmin && permissions?.user.is_admin ? (
            <AttackTypes />
          ) : (
            <Box>
              <Typography variant="h6">Access Denied</Typography>
              <Typography variant="body2" color="text.secondary">
                Only super administrators can view attack types management.
              </Typography>
            </Box>
          )}
        </TabPanel>
        )}

        {hasPermission('view_audit_log') && (
          <TabPanel value={tabValue} index={auditLogTabIndex}>
            <AuditLog />
          </TabPanel>
        )}

      </Paper>

      {/* Partner Dialog */}
      <Dialog open={partnerDialogOpen} onClose={handlePartnerDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPartner ? 'Edit Partner' : 'Add Partner'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Partner Name"
            fullWidth
            variant="outlined"
            value={partnerForm.name}
            onChange={(e) => setPartnerForm({ name: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePartnerDialogClose}>Cancel</Button>
          <Button onClick={handlePartnerSubmit} variant="contained">
            {editingPartner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Dialog */}
      <Dialog open={tenantDialogOpen} onClose={handleTenantDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Add Tenant'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tenant Name"
            fullWidth
            variant="outlined"
            value={tenantForm.name}
            onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Partner"
            fullWidth
            variant="outlined"
            value={tenantForm.partner_id}
            onChange={(e) => setTenantForm({ ...tenantForm, partner_id: e.target.value })}
            SelectProps={{
              native: true,
            }}
          >
            <option value="">No Partner</option>
            {partners.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partner.name}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTenantDialogClose}>Cancel</Button>
          <Button onClick={handleTenantSubmit} variant="contained">
            {editingTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <TenantDeleteDialog
        open={deleteDialogOpen}
        tenantId={tenantToDelete?.id || ''}
        tenantName={tenantToDelete?.name || ''}
        onClose={handleDeleteDialogClose}
        onDelete={handleDeleteConfirm}
      />

        </Box>
      </PageTransition>
    </PermissionGate>
  );
};

export default Partners; 